/**
 * useTimelineMarkers
 *
 * Builds the set of inline timeline markers (intro card, end card, nametags)
 * that should be rendered inside the transcript view.
 *
 * Markers are derived from:
 *   - `editor.timeline.allElements` — top-level timeline elements with `time` / `end_time`
 *   - `currentVideo.extraElementData[elementId].compositionType` — identifies cards/nametags
 *   - `currentVideo.extraElementData['video_template_settings'].videoTemplateElements` —
 *     supplies speaker labels for nameCard elements
 *
 * Nametag markers are keyed to the transcript element index whose `[ts, end_ts)` range
 * contains the marker's `time`. Cards are returned in separate intro/end buckets.
 */

import {
  PLACEMENT,
  placementUsesTranscriptAnchor,
} from '@src/editor/lib/placementEvaluation';
import { getElementMetaObject } from '@src/utility/extraElementData';
import { useMemo } from 'react';

import type { Element } from '@src/editor/models/Element';
import type { VideoLike } from '@src/utility/extraElementData';

// ---------------------------------------------------------------------------
// Narrow interfaces — keep the hook decoupled from specific store implementations
// ---------------------------------------------------------------------------

/** Minimal shape needed to access video metadata (extraElementData). */
export interface VideoCreatorStoreLike {
  currentVideo?: VideoLike;
}

/** Minimal editor store shape needed for timeline elements + transcript. */
export interface EditorStoreLike {
  editor: {
    timeline: {
      allElements: readonly Element[];
    };
  };
  transcriptElements: ReadonlyArray<unknown> | undefined;
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type TimelineMarkerType = 'introCard' | 'endCard' | 'nameCard' | 'overlay';

export type TimelineMarker = {
  id: string;
  type: TimelineMarkerType;
  time: number;
  endTime: number;
  /** For nameCards only: internal speaker label (e.g., 'spk_0'). */
  speakerLabel?: string;
  /** Composition template label from Dato CMS, when available. */
  label?: string;
  /**
   * Overlays added from a transcript selection store the words they cover by
   * stable source `initial_index` (`transcriptAnchor` in extraElementData).
   * When present, the transcript underline is mapped from these stable indices
   * instead of the overlay's fixed time window, so it stays on the same words
   * through later edits (ripple-delete shifts the words' timestamps but not
   * their `initial_index`). Absent → fall back to the time-window mapping.
   */
  anchor?: { startInitialIndex: number; endInitialIndex: number };
  /**
   * Overlays only: the authored timeline placement (`'clip'` / `'selection'` /
   * `'clip-start'` / …), straight off the element's meta. Read by
   * `overlayShowsTranscriptUnderline`.
   */
  placement?: string;
  /**
   * Overlays only: some `aiText` control on this graphic reads the SELECTION
   * base-text scope — see `readsSelectionBaseText`.
   */
  readsSelectionBaseText?: boolean;
};

/**
 * PLAT-10921 — whether this overlay draws the transcript underline.
 *
 * A `clip`-placed graphic spans the clip by definition, so its underline covers
 * every word of the transcript: it marks nothing out, and it buries the
 * underlines that do. The exception is a graphic whose `aiText` control reads
 * the SELECTION — placement and base-text scope are independent axes, and there
 * the underline is the only thing on screen saying which words the model will
 * actually read, so it stays.
 *
 * Dropping the marker here drops the hover popover and the range-adjust handles
 * with it, which is the point: both act on a transcript range, and a
 * clip-placed graphic takes its range from the placement, not from words.
 */
export function overlayShowsTranscriptUnderline(
  marker: TimelineMarker,
): boolean {
  return (
    marker.placement !== PLACEMENT.CLIP ||
    marker.readsSelectionBaseText === true
  );
}

export type ClipTimelineMarkers = {
  intro: TimelineMarker[];
  end: TimelineMarker[];
  /** Map of transcript element index -> nametag markers anchored to that word. */
  nametagsByIndex: Map<number, TimelineMarker[]>;
  /**
   * PLAT-10045 — non-card composition placements ('overlay' | 'other' |
   * 'quoteCard') surfaced as time-range markers for the clip-preview
   * transcript underline.
   */
  overlays: TimelineMarker[];
};

const EMPTY_MARKERS: ClipTimelineMarkers = {
  intro: [],
  end: [],
  nametagsByIndex: new Map(),
  overlays: [],
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * PLAT-10921 — does any `aiText` control on this graphic read the SELECTION?
 *
 * The scope the LAST GENERATION ran with when there is one, the template's
 * authored default before the first generation — the same precedence the AI
 * panel itself shows (`AITextControlPanelAdapter`), so the underline agrees
 * with the scope the creator is looking at rather than with the card's
 * authoring. Flipping the panel's Base text toggle therefore moves the
 * underline on the next Regenerate, which is when the choice becomes the
 * graphic's rather than a pending edit.
 *
 * `selection` is the default for a control that names no scope, matching every
 * other reader of `defaultBaseTextScope`.
 */
function readsSelectionBaseText(meta: Record<string, unknown>): boolean {
  const controls = meta.compositionControls;
  if (!Array.isArray(controls)) return false;
  const generated = isRecord(meta.aiTextControls) ? meta.aiTextControls : {};

  return controls.some(control => {
    if (!isRecord(control) || control.type !== 'aiText') return false;
    const controlId = typeof control.id === 'string' ? control.id : null;
    const provenance = controlId ? generated[controlId] : undefined;
    const generatedScope = isRecord(provenance)
      ? provenance.baseTextScope
      : undefined;
    const scope = generatedScope ?? control.defaultBaseTextScope ?? 'selection';
    return scope === 'selection';
  });
}

function extractSpeakerLabelMap(video: VideoLike): Map<string, string> {
  const out = new Map<string, string>();
  const settings = getElementMetaObject(video, 'video_template_settings');
  if (!settings) return out;
  const entries = settings['videoTemplateElements'];
  if (!Array.isArray(entries)) return out;
  for (const raw of entries) {
    if (!isRecord(raw)) continue;
    if (raw.type !== 'nameCard') continue;
    const id = typeof raw.id === 'string' ? raw.id : null;
    const speakerLabel =
      typeof raw.speakerLabel === 'string' ? raw.speakerLabel : null;
    if (id && speakerLabel) out.set(id, speakerLabel);
  }
  return out;
}

/**
 * Find the transcript element index whose timestamp range covers `time`.
 * Falls back to the first element with `ts >= time` if no range matches.
 * Returns -1 when `time` is past the last element.
 */
function findIndexForTime(
  transcriptElements: ReadonlyArray<unknown>,
  time: number,
): number {
  let firstAfter = -1;
  for (let i = 0; i < transcriptElements.length; i++) {
    const el = transcriptElements[i];
    if (!isRecord(el)) continue;
    const ts = typeof el.ts === 'number' ? el.ts : null;
    const endTs = typeof el.end_ts === 'number' ? el.end_ts : null;
    if (ts == null) continue;
    if (endTs != null && ts <= time && time < endTs) return i;
    if (firstAfter === -1 && ts >= time) firstAfter = i;
  }
  return firstAfter;
}

/**
 * Find the transcript element to anchor a nametag chip to. When the marker
 * carries a `speakerLabel`, anchor to the first visible text element of the
 * matching speaker section (paragraph stretch) closest to `marker.time` —
 * the nametag semantically introduces that speaker, so the chip should land
 * inside their section rather than at the tail of the previous speaker's.
 * Falls back to plain time-based lookup when no speaker info is available.
 */
function findNametagAnchorIndex(
  transcriptElements: ReadonlyArray<unknown>,
  marker: TimelineMarker,
): number {
  const target = marker.speakerLabel;
  if (!target) return findIndexForTime(transcriptElements, marker.time);

  let prevSpeaker: string | null = null;
  let bestIdx = -1;
  let bestDist = Infinity;

  for (let i = 0; i < transcriptElements.length; i++) {
    const el = transcriptElements[i];
    if (!isRecord(el)) continue;
    if (el.type !== 'text') continue;
    const speaker =
      typeof el.speaker_label === 'string' ? el.speaker_label : null;
    const ts = typeof el.ts === 'number' ? el.ts : null;
    const state = typeof el.state === 'string' ? el.state : null;
    const isVisible = state !== 'removed' && state !== 'cut';

    const startsNewStretch = speaker !== prevSpeaker;
    prevSpeaker = speaker;

    if (!startsNewStretch || speaker !== target || ts == null) continue;

    // Anchor to the first VISIBLE word of this stretch; if the section
    // starts with cut/removed words, walk forward to the first visible one.
    let anchorIdx = i;
    if (!isVisible) {
      let scan = i + 1;
      let found = -1;
      while (scan < transcriptElements.length) {
        const next = transcriptElements[scan];
        if (isRecord(next) && next.type === 'text') {
          const nextSpeaker =
            typeof next.speaker_label === 'string' ? next.speaker_label : null;
          if (nextSpeaker !== target) break;
          const nextState = typeof next.state === 'string' ? next.state : null;
          if (nextState !== 'removed' && nextState !== 'cut') {
            found = scan;
            break;
          }
        }
        scan++;
      }
      if (found === -1) continue;
      anchorIdx = found;
    }

    const anchorEl = transcriptElements[anchorIdx];
    const anchorTs =
      isRecord(anchorEl) &&
      anchorEl.type === 'text' &&
      typeof anchorEl.ts === 'number'
        ? anchorEl.ts
        : null;
    if (anchorTs == null) continue;
    const dist = Math.abs(anchorTs - marker.time);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = anchorIdx;
    }
  }

  if (bestIdx === -1) return findIndexForTime(transcriptElements, marker.time);
  return bestIdx;
}

// ---------------------------------------------------------------------------
// Pure builder (testable without React)
// ---------------------------------------------------------------------------

export function buildTimelineMarkers(input: {
  elements: readonly Element[];
  video: VideoLike;
  transcriptElements: ReadonlyArray<unknown>;
}): ClipTimelineMarkers {
  const { elements, video, transcriptElements } = input;
  if (!elements?.length || !video) return EMPTY_MARKERS;

  const speakerLabelById = extractSpeakerLabelMap(video);

  const intro: TimelineMarker[] = [];
  const end: TimelineMarker[] = [];
  const nametags: TimelineMarker[] = [];
  const overlays: TimelineMarker[] = [];

  for (const el of elements) {
    const id = el.id;
    if (!id) continue;
    const meta = getElementMetaObject(video, id);
    if (!meta) continue;
    const compositionType = meta.compositionType;
    if (
      compositionType !== 'introCard' &&
      compositionType !== 'endCard' &&
      compositionType !== 'nameCard' &&
      compositionType !== 'overlay' &&
      compositionType !== 'other' &&
      compositionType !== 'quoteCard'
    ) {
      continue;
    }
    const time = el.time;
    const endTime = el.end_time;
    const label =
      typeof meta.templateLabel === 'string' ? meta.templateLabel : undefined;
    if (
      compositionType === 'overlay' ||
      compositionType === 'other' ||
      compositionType === 'quoteCard'
    ) {
      const marker: TimelineMarker = {
        id,
        type: 'overlay',
        time,
        endTime,
      };
      if (label) marker.label = label;
      // PLAT-10921 — the two facts `overlayShowsTranscriptUnderline` decides on.
      if (typeof meta.placement === 'string') marker.placement = meta.placement;
      if (readsSelectionBaseText(meta)) marker.readsSelectionBaseText = true;
      // PLAT-10921 — a `clip-start` / `clip-end` graphic keeps the anchor of the
      // passage it was created from, but its range runs from the clip's bound to
      // that passage's edge. Honouring the anchor would underline only the
      // original selection and contradict the graphic on the timeline, so those
      // two map from the time window instead.
      const anchor = placementUsesTranscriptAnchor(marker.placement)
        ? meta.transcriptAnchor
        : undefined;
      if (
        isRecord(anchor) &&
        typeof anchor.startInitialIndex === 'number' &&
        typeof anchor.endInitialIndex === 'number'
      ) {
        marker.anchor = {
          startInitialIndex: anchor.startInitialIndex,
          endInitialIndex: anchor.endInitialIndex,
        };
      }
      overlays.push(marker);
      continue;
    }
    const marker: TimelineMarker = {
      id,
      type: compositionType,
      time,
      endTime,
    };
    if (label) marker.label = label;
    if (compositionType === 'introCard') {
      intro.push(marker);
    } else if (compositionType === 'endCard') {
      end.push(marker);
    } else {
      const speakerLabel = speakerLabelById.get(id);
      if (speakerLabel) marker.speakerLabel = speakerLabel;
      nametags.push(marker);
    }
  }

  if (
    intro.length === 0 &&
    end.length === 0 &&
    nametags.length === 0 &&
    overlays.length === 0
  ) {
    return EMPTY_MARKERS;
  }

  intro.sort((a, b) => a.time - b.time);
  end.sort((a, b) => a.time - b.time);
  nametags.sort((a, b) => a.time - b.time);
  overlays.sort((a, b) => a.time - b.time);

  const nametagsByIndex = new Map<number, TimelineMarker[]>();
  for (const marker of nametags) {
    const idx = findNametagAnchorIndex(transcriptElements, marker);
    if (idx === -1) continue;
    const bucket = nametagsByIndex.get(idx);
    if (bucket) {
      bucket.push(marker);
    } else {
      nametagsByIndex.set(idx, [marker]);
    }
  }

  return { intro, end, nametagsByIndex, overlays };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export function useTimelineMarkers(
  videoCreatorStore: VideoCreatorStoreLike | null,
  editorStore: EditorStoreLike | null,
): ClipTimelineMarkers {
  const elements = editorStore?.editor?.timeline?.allElements;
  const video: VideoLike = videoCreatorStore?.currentVideo;
  const extraElementData = videoCreatorStore?.currentVideo?.extraElementData;
  const transcriptElements = editorStore?.transcriptElements;

  return useMemo(() => {
    if (!elements || !transcriptElements?.length) return EMPTY_MARKERS;
    return buildTimelineMarkers({
      elements,
      video,
      transcriptElements,
    });
  }, [elements, video, extraElementData, transcriptElements]);
}
